import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
    User,
    ApiResponse,
    UserMe,
    ProfileMeDto,
    UpdateMyProfilePayload,
    UpdateMyProfileResponse,
    ChangePinPayload,
    GetAllUsersResponse,
    UserRow,
    UpdateAccountStatusPayload,
    UpdateAccountStatusResponse,
    AdminCreateUserPayload,
    AdminCreateUserResponse,
} from '../models/auth.models';
import { baseURL } from '../constants/base.url';
import { map } from 'rxjs/operators';
import { throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class UserProfileService {

    constructor(private http: HttpClient) { }

    // Update password method
    updatePassword(currentPassword: string, newPassword: string): Observable<any> {
        const body = { currentPassword, newPassword };
        return this.http.put(`${baseURL}auth/update-password`, body).pipe(
            catchError(this.handleError)
        );
    }

    // ✅ UPDATE MY PROFILE
    updateMyProfile(payload: UpdateMyProfilePayload, file?: File) {
        const fd = new FormData();

        // text fields
        fd.append('fullName', payload.fullName || '');
        if (payload.email != null) fd.append('email', payload.email);
        fd.append('phoneNo', payload.phoneNo || '');
        if (payload.city != null) fd.append('city', payload.city);
        if (payload.address != null) fd.append('address', payload.address);

        // file
        if (file) fd.append('profileImage', file);

        return this.http.put<ApiResponse<UpdateMyProfileResponse>>(
            `${baseURL}auth/update-profile`,
            fd
        ).pipe(
            map(res => res.data),
            catchError(this.handleError)
        );
    }


    // ✅ CHANGE PIN
    changePin(payload: ChangePinPayload): Observable<void> {
        return this.http.put<ApiResponse<null>>(`${baseURL}auth/change-pin`, payload).pipe(
            map(() => void 0),
            catchError(this.handleError)
        );
    }


    // Forget password method (for reset token)
    forgetPassword(email: string): Observable<any> {
        return this.http.post(`${baseURL}auth/forgot-pin`, { email }).pipe(
            catchError(this.handleError)
        );
    }

    // Reset PIN method
    resetPin(otp: string, newPin: string): Observable<any> {
        return this.http.post(`${baseURL}auth/reset-pin`, {
            otp,
            newPin
        }).pipe(
            catchError(this.handleError)
        );
    }

    // Get current user profile
    getUserProfile(): Observable<UserMe> {
        return this.http.get<ApiResponse<ProfileMeDto>>(`${baseURL}auth/me`).pipe(
            map((res) => {
                const d = res.data;

                const user: UserMe = {
                    _id: d.basicInfo.id,
                    role: d.basicInfo.role,

                    patientId: d.basicInfo.patientId ?? null,

                    fullName: d.basicInfo.fullName,
                    email: d.basicInfo.email ?? null,
                    phoneNo: d.basicInfo.phoneNo,

                    profileImage: d.basicInfo.profileImage ?? null,

                    city: d.meta?.city ?? null,
                    address: d.meta?.address ?? null,

                    accountStatus: d.account.status,

                    isOnline: d.presence?.isOnline ?? false,
                    lastSeen: d.presence?.lastSeen ?? null,

                    lastLoginAt: d.security?.lastLoginAt ?? null,
                    pinChangedAt: d.security?.pinChangedAt ?? null,

                    devices: d.devices ?? [],

                    createdAt: d.account.createdAt,
                    updatedAt: d.account.updatedAt ?? d.account.createdAt,
                };

                return user;
            }),
            catchError(this.handleError)
        );
    }

    // Create User
    createUser(userData: FormData): Observable<any> {
        return this.http.post(`${baseURL}auth/register`, userData)
    }

    // Delete user by ID (admin only)
    deleteUser(userId: string): Observable<any> {
        return this.http.delete(`${baseURL}auth/user/delete/${userId}`).pipe(
            catchError(this.handleError)
        );
    }

    // Update user profile
    updateUserProfile(formData: FormData): Observable<any> {
        return this.http.put(`${baseURL}auth/update`, formData).pipe(
            catchError(this.handleError)
        );
    }

    // Update user profile
    updateUsers(userId: string, updatedData: any): Observable<any> {
        return this.http.put(`${baseURL}auth/admin/user/${userId}`, updatedData).pipe(
            catchError(this.handleError)
        );
    }


    // Get single user (admin only)
    getSingleUser(userId: string): Observable<any> {
        return this.http.get(`${baseURL}auth/user/${userId}`).pipe(
            catchError(this.handleError)
        );
    }

    // Admin Manage Users
    // ✅ 1) GET ALL USERS (pagination + filters)
    getAllUsers(params: {
        page?: number;
        limit?: number;
        search?: string;
        accountStatus?: string;
        role?: string;
    }): Observable<GetAllUsersResponse> {
        let hp = new HttpParams();
        if (params.page) hp = hp.set('page', params.page);
        if (params.limit) hp = hp.set('limit', params.limit);
        if (params.search) hp = hp.set('search', params.search);
        if (params.accountStatus) hp = hp.set('accountStatus', params.accountStatus);
        if (params.role) hp = hp.set('role', params.role);

        return this.http
            .get<ApiResponse<GetAllUsersResponse>>(`${baseURL}auth/users`, { params: hp })
            .pipe(map(res => res.data), catchError(this.handleError));
    }

    // ✅ 2) GET USER BY ID
    getUserById(userId: string): Observable<UserRow> {
        return this.http
            .get<ApiResponse<UserRow>>(`${baseURL}auth/users/${userId}`)
            .pipe(map(res => res.data), catchError(this.handleError));
    }

    // ✅ 3) UPDATE ACCOUNT STATUS
    updateAccountStatus(payload: UpdateAccountStatusPayload): Observable<UpdateAccountStatusResponse> {
        return this.http
            .put<any>(`${baseURL}auth/update-account-status`, payload)
            .pipe(
                // backend response structure: { status: 200, message, data: { userId, accountStatus } }
                map(res => res.data as UpdateAccountStatusResponse),
                catchError(this.handleError)
            );
    }

    // ✅ 4) ADMIN CREATE USER (signup + profileImage)
    adminCreateUser(payload: AdminCreateUserPayload, file?: File) {
        const fd = new FormData();
        fd.append('fullName', payload.fullName);
        fd.append('phoneNo', payload.phoneNo);
        fd.append('pin', payload.pin);

        if (payload.role) fd.append('role', payload.role);
        if (payload.email != null) fd.append('email', payload.email);
        if (payload.city != null) fd.append('city', payload.city);
        if (payload.address != null) fd.append('address', payload.address);

        if (file) fd.append('profileImage', file);

        return this.http
            .post<ApiResponse<AdminCreateUserResponse>>(`${baseURL}auth/signup`, fd)
            .pipe(
                map(res => res.data)
            );
    }

    // Handle errors during HTTP requests
    private handleError(error: any) {
        let errorMessage = 'An unknown error occurred!';
        if (error.error instanceof ErrorEvent) {
            errorMessage = `An error occurred: ${error.error.message}`;
        } else {
            errorMessage = `Server returned code: ${error.status}, error message is: ${error.message}`;
        }
        return throwError(errorMessage); // Use throwError instead of Observable.throw
    }
}